/**
 * AWS Cognito Authentication Service
 * Handles user authentication, registration, and session management
 */

import { Amplify } from 'aws-amplify';
import { signUp, confirmSignUp, signIn, signOut, signInWithRedirect, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import config from '../config/config';

// Determine redirect URIs dynamically based on current origin
const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
const redirectSignIn = `${currentOrigin}/callback`;
const redirectSignOut = `${currentOrigin}/`;

// Configure Amplify with Cognito settings (including OAuth for Google)
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.cognito.userPoolId,
      userPoolClientId: config.cognito.clientId,
      loginWith: {
        email: true,
        oauth: {
          domain: config.cognito.domain,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [redirectSignIn],
          redirectSignOut: [redirectSignOut],
          responseType: 'code',
          providers: ['Google'],
        },
      },
    },
  },
});

export const cognitoService = {
  /**
   * Sign up a new user
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @param {string} name - User's full name
   * @returns {Promise} Sign up result
   */
  async signUp(email, password, name) {
    try {
      const { userId, isSignUpComplete, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
          },
        },
      });

      return {
        userId,
        isSignUpComplete,
        nextStep,
      };
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  },

  /**
   * Confirm email verification code
   * @param {string} email - User's email address
   * @param {string} code - Verification code from email
   * @returns {Promise} Confirmation result
   */
  async confirmSignUp(email, code) {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: email,
        confirmationCode: code,
      });

      return {
        isSignUpComplete,
        nextStep,
      };
    } catch (error) {
      console.error('Error confirming sign up:', error);
      throw error;
    }
  },

  /**
   * Sign in a user
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise} Sign in result with user info
   */
  async signIn(email, password) {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
      });

      if (isSignedIn) {
        // Get user attributes after successful sign in
        const user = await getCurrentUser();
        return {
          isSignedIn,
          user,
          nextStep,
        };
      }

      return {
        isSignedIn,
        nextStep,
      };
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  },

  /**
   * Sign in with Google OAuth via Cognito federated sign-in
   * This redirects the user to Cognito hosted UI -> Google -> back to app
   */
  async signInWithGoogle() {
    try {
      await signInWithRedirect({ provider: 'Google' });
      // Note: This will redirect the page, so code after this won't execute
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  },

  /**
   * Sign out the current user
   * @returns {Promise}
   */
  async signOut() {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  },

  /**
   * Get the current authenticated user
   * @returns {Promise} Current user info
   */
  async getCurrentUser() {
    try {
      const user = await getCurrentUser();
      return user;
    } catch (error) {
      // User is not authenticated
      return null;
    }
  },

  /**
   * Get current session with JWT tokens
   * @returns {Promise} Session object with tokens
   */
  async getSession() {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error('Error getting session:', error);
      throw error;
    }
  },

  /**
   * Get the Cognito access token (needed for user self-management APIs like DeleteUser)
   * @returns {Promise<string>} Cognito access token
   */
  async getAccessToken() {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) {
        throw new Error('No access token available');
      }
      return accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  },

  /**
   * Get the JWT ID token from current session
   * @returns {Promise<string>} JWT ID token
   */
  async getIdToken() {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No ID token available');
      }

      return idToken;
    } catch (error) {
      console.error('Error getting ID token:', error);
      throw error;
    }
  },

  /**
   * Get user attributes from current session
   * @returns {Promise} User attributes (sub, email, name, etc.)
   */
  async getUserAttributes() {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      // Extract user attributes from ID token
      const idToken = session.tokens?.idToken;
      const attributes = idToken?.payload || {};

      // Fallback strategy for name:
      // 1. Use 'name' if available
      // 2. Combine 'given_name' and 'family_name' if available
      // 3. Use 'email' username (part before @)
      let displayName = attributes.name;

      if (!displayName && (attributes.given_name || attributes.family_name)) {
        displayName = [attributes.given_name, attributes.family_name]
          .filter(Boolean)
          .join(' ')
          .trim();
      }

      if (!displayName && attributes.email) {
        displayName = attributes.email.split('@')[0];
      }

      if (!displayName) {
        displayName = 'User'; // Ultimate fallback
      }

      return {
        sub: attributes.sub,
        email: attributes.email,
        name: displayName,
        emailVerified: attributes.email_verified,
      };
    } catch (error) {
      console.error('Error getting user attributes:', error);
      throw error;
    }
  },
};

export default cognitoService;
